import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ASSETS } from "../lib/assets";
import { Check } from "lucide-react";

const VALUES = [
  {
    title: "Leidenschaft",
    blurb:
      "Wir lieben, was wir tun. Jedes Event ist für uns mehr als nur ein Job — es ist unsere Bühne.",
    // orbit config
    radius: 44,   // % of container half-size — distance from logo center
    duration: 28, // seconds for one full orbit
    startAngle: 0,
    direction: 1, // +1 clockwise, -1 counter
  },
  {
    title: "Präzision",
    blurb:
      "Technik, die funktioniert. Immer. Wir planen akribisch und setzen makellos um.",
    radius: 50,
    duration: 38,
    startAngle: 95,
    direction: -1,
  },
  {
    title: "Kreativität",
    blurb:
      "Standardlösungen? Nicht mit uns. Wir denken weiter und schaffen einzigartige Erlebnisse.",
    radius: 41,
    duration: 32,
    startAngle: 200,
    direction: 1,
  },
  {
    title: "Menschlichkeit",
    blurb:
      "Bei uns zählt der Mensch. Ehrliche Kommunikation und echte Partnerschaft.",
    radius: 53,
    duration: 44,
    startAngle: 290,
    direction: -1,
  },
];

const CHECKS = [
  "Modernste Technik",
  "Erfahrenes Team",
  "Flexible Lösungen",
  "Faire Preise",
  "Rundum-Service",
];

export const About = () => {
  const reduce = useReducedMotion();

  return (
    <section
      id="about"
      data-testid="about-section"
      data-glow="about"
      className="relative z-10 px-6 sm:px-10 lg:px-[7rem] py-28 lg:py-36 max-w-[1500px] mx-auto"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1fr] gap-14 lg:gap-20 items-center">
        {/* LEFT — chips orbit around glowing logo */}
        <motion.div
          className="relative w-full aspect-square max-w-[400px] sm:max-w-[560px] mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Ambient halo */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at center, rgba(244,152,0,0.32), rgba(231,22,129,0.10) 35%, transparent 60%)",
              filter: "blur(40px)",
            }}
          />
          {/* Concentric rings */}
          {[1, 2, 3].map((r) => (
            <motion.div
              key={r}
              className="absolute rounded-full"
              style={{
                inset: `${r * 6}%`,
                border: "1px solid rgba(244,152,0,0.06)",
              }}
              animate={reduce ? {} : { rotate: [0, r % 2 === 0 ? 360 : -360] }}
              transition={{ duration: 60 + r * 12, repeat: Infinity, ease: "linear" }}
            />
          ))}

          {/* Logo center */}
          <motion.div
            className="absolute inset-[26%] flex items-center justify-center"
            animate={reduce ? {} : { scale: [1, 1.04, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            <img
              src={ASSETS.logo}
              alt="Monoton Mark"
              className="w-full h-auto"
              style={{
                mixBlendMode: "screen",
                filter: "drop-shadow(0 0 60px rgba(244,152,0,0.55)) drop-shadow(0 0 28px rgba(231,22,129,0.35))",
              }}
            />
          </motion.div>

          {/* Orbiting chips — each chip rides a circular path around the logo */}
          {VALUES.map((v, i) => (
            <OrbitChip key={v.title} value={v} index={i} reduce={reduce} />
          ))}
        </motion.div>

        {/* RIGHT — copy + value-cards + checks */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(244,152,0,0.18)",
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: "var(--amber)", boxShadow: "0 0 8px var(--amber)" }}
            />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber">
              Warum monoton.
            </span>
          </div>

          <h2 className="h2-display mb-6">
            Nicht einfach Technik.
            <br />
            <span className="gradient-text">Sondern Erlebnis.</span>
          </h2>

          <p className="body-mute text-base lg:text-lg leading-[1.7] max-w-[52ch] mb-10">
            Was uns antreibt, ist mehr als perfekte Technik. Es ist die
            Leidenschaft, Menschen zusammenzubringen und Momente zu schaffen,
            die in Erinnerung bleiben.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7 mb-12">
            {VALUES.map((v) => (
              <div key={v.title}>
                <h3 className="font-display text-lg lg:text-xl font-semibold mb-2">
                  {v.title}
                </h3>
                <p className="body-mute text-sm leading-[1.65] max-w-[34ch]">
                  {v.blurb}
                </p>
              </div>
            ))}
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 pt-7"
            style={{ borderTop: "1px solid rgba(244,152,0,0.12)" }}
          >
            {CHECKS.map((c) => (
              <div key={c} className="flex items-center gap-2.5">
                <span
                  className="flex items-center justify-center rounded-full"
                  style={{
                    width: 18,
                    height: 18,
                    background: "rgba(244,152,0,0.15)",
                    border: "1px solid rgba(244,152,0,0.4)",
                  }}
                >
                  <Check size={11} className="text-amber" strokeWidth={2.5} />
                </span>
                <span className="font-display text-sm text-offwhite/85">{c}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/**
 * OrbitChip — moves on a circular path around the parent center.
 *  - Outer wrapper is sized as a circle (2*radius% of container) and rotates
 *  - The chip sits at the top of that circle (12 o'clock)
 *  - The chip itself counter-rotates so the text always stays upright
 *  - Plus a subtle vertical bob for life
 */
const OrbitChip = ({ value, index, reduce }) => {
  const { title, radius, duration, startAngle, direction } = value;
  const dir = direction === 1 ? 360 : -360;
  const size = `${radius * 2}%`;

  return (
    <motion.div
      className="absolute pointer-events-none hidden sm:block"
      style={{
        top: "50%",
        left: "50%",
        width: size,
        height: size,
        marginLeft: `-${radius}%`,
        marginTop: `-${radius}%`,
        transformOrigin: "center center",
      }}
      initial={{ rotate: startAngle, opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-80px" }}
      animate={
        reduce
          ? { rotate: startAngle }
          : { rotate: [startAngle, startAngle + dir] }
      }
      transition={{
        rotate: {
          duration,
          repeat: Infinity,
          ease: "linear",
        },
        opacity: { duration: 0.8, delay: 0.3 + index * 0.12 },
      }}
    >
      {/* Chip sits at top (12 o'clock) of the orbit circle */}
      <div
        className="absolute"
        style={{
          top: 0,
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        {/* Counter-rotate so the chip stays upright while orbiting */}
        <motion.div
          className="pointer-events-auto"
          animate={
            reduce ? { rotate: 0 } : { rotate: [-startAngle, -startAngle - dir] }
          }
          transition={{
            duration,
            repeat: Infinity,
            ease: "linear",
          }}
        >
          {/* Subtle bob */}
          <motion.span
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-display text-sm whitespace-nowrap"
            style={{
              background: "rgba(15,15,18,0.85)",
              border: "1px solid rgba(244,152,0,0.22)",
              color: "var(--offwhite)",
              backdropFilter: "blur(8px)",
              boxShadow:
                "0 8px 24px rgba(0,0,0,0.45), 0 0 18px rgba(244,152,0,0.10)",
            }}
            animate={reduce ? {} : { y: [0, -4, 0] }}
            transition={{
              duration: 4 + index * 0.4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: index * 0.5,
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{
                background: "var(--amber)",
                boxShadow: "0 0 8px var(--amber)",
              }}
            />
            {title}
          </motion.span>
        </motion.div>
      </div>
    </motion.div>
  );
};
